import { getTranslation } from '@easy-expense/intl-client';
import { Layout, OpenSans, Separator, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import { CheckoutSummary, formatAmount, formatRecurring } from '../../lib/Billing/checkoutSummary';

/**
 * Indent that lines the totals up under the line item's text rather than its icon (44px icon +
 * 14px gutter). Every checkout summary leads with the same icon-and-title row, so the figure
 * column reads as one block down the whole pane.
 */
const SUMMARY_INSET = 58;

const NOWRAP = { whiteSpace: 'nowrap', flexShrink: 0 } as const;

// A money value never wraps (a currency prefix like "CA$" makes it long enough to try); the
// label gives way instead.
const SummaryRow: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
}> = ({ label, value, bold, muted }) => {
  const weight = bold ? 'bold-700' : undefined;
  const Value = muted ? OpenSans.Secondary : OpenSans.Primary;
  return (
    <Layout.Row align justify="space-between" py={9}>
      <OpenSans.Primary size="s-16" weight={weight} style={{ flexShrink: 1 }}>
        {label}
      </OpenSans.Primary>
      <Spacer.Horizontal size={12} />
      <Value size="s-16" weight={weight} style={NOWRAP}>
        {value}
      </Value>
    </Layout.Row>
  );
};

/**
 * The figures below a checkout's line item: subtotal, what the buyer applied or owes on top, and
 * the two numbers that decide the purchase -- what is charged today and what each renewal costs.
 *
 * `live` says whether these are Paddle's own figures or the page's opening estimate, which is the
 * difference between a tax line worth showing and a zero placeholder.
 */
export const CheckoutTotals: React.FC<{ figures: CheckoutSummary; live: boolean }> = ({
  figures,
  live,
}) => {
  const money = (amount: number) => formatAmount(amount, figures.currencyCode);
  // Tax is folded into the totals; a separate line only earns its place once Paddle has an
  // address and the amount is real.
  const showTax = live && figures.tax > 0;
  const showDiscount = live && figures.discount > 0;
  const stale = figures.repricing;
  // A renewal line is worth its space only when it differs from what is being charged now --
  // during a trial, or on a first period that is discounted or prorated. Its label follows the
  // same fact, so a buyer who is not being sold a trial is never told what happens "after" one.
  const showRenewal = figures.trialEndsOn !== null || figures.recurringTotal !== figures.dueToday;
  let renewalLabel = getTranslation('Total per renewal');
  if (figures.trialEndsOn !== null) {
    renewalLabel = getTranslation('Total after trial');
  }

  return (
    <Layout.Column style={{ marginLeft: SUMMARY_INSET }}>
      <Separator.Horizontal />
      <Spacer.Vertical size={10} />
      <SummaryRow
        label={getTranslation('Subtotal')}
        value={money(figures.subtotal)}
        muted={stale}
      />
      {showDiscount ? (
        <SummaryRow
          label={getTranslation('Discount')}
          value={`−${money(figures.discount)}`}
          muted={stale}
        />
      ) : null}
      {showTax ? (
        <SummaryRow label={getTranslation('Tax')} value={money(figures.tax)} muted={stale} />
      ) : null}
      <Spacer.Vertical size={10} />
      <Separator.Horizontal />
      <Spacer.Vertical size={10} />
      {showRenewal ? (
        <SummaryRow
          label={renewalLabel}
          value={formatRecurring(figures.recurringTotal, figures)}
          muted={stale}
        />
      ) : null}
      <SummaryRow
        label={getTranslation('Total due today')}
        value={money(figures.dueToday)}
        bold
        muted={stale}
      />
      {stale ? (
        <OpenSans.Secondary size="xs-12">
          {getTranslation('Updating your total…')}
        </OpenSans.Secondary>
      ) : null}
      {live ? null : (
        <OpenSans.Secondary size="xs-12">
          {getTranslation('Prices shown in USD until the payment form loads.')}
        </OpenSans.Secondary>
      )}
    </Layout.Column>
  );
};
